import './App.css';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardHeader from './dashboardheader.js'
import VerticalNavigation from './VerticalNavigation.js'
import bhcalc from './bhcalc.js'

function Admin() {
  const navigate = useNavigate();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationFormData, setLocationFormData] = useState({
    name: '',
    address: '',
    zipcode: '',
    logoUrl: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [locationError, setLocationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(true);
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(true);
  const [programTab, setProgramTab] = useState('active');
  const [showProgramModal, setShowProgramModal] = useState(false);
  const [programFormData, setProgramFormData] = useState({
    programName: '',
    programType: ''
  });
  const [programError, setProgramError] = useState('');
  const [isProgramSubmitting, setIsProgramSubmitting] = useState(false);
  const [programTypes, setProgramTypes] = useState([]);
  const [, setProgramTypesLoading] = useState(false);
  const [programImageFile, setProgramImageFile] = useState(null);
  const [programImagePreview, setProgramImagePreview] = useState(null);
  const [partners, setPartners] = useState([]);
  const [partnersLoading, setPartnersLoading] = useState(true);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [partnerFormData, setPartnerFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    locationId: ''
  });
  const [partnerError, setPartnerError] = useState('');
  const [isPartnerSubmitting, setIsPartnerSubmitting] = useState(false);

  const fetchProgramTypes = useCallback(async () => {
    try {
      setProgramTypesLoading(true);
      const { data } = await bhcalc.get('/program-type');
      setProgramTypes(data);
    } catch (err) {
      console.error('Error fetching program types:', err);
    } finally {
      setProgramTypesLoading(false);
    }
  }, []);

  const fetchLocations = useCallback(async () => {
    try {
      setLocationsLoading(true);
      const { data } = await bhcalc.get('/location');
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    } finally {
      setLocationsLoading(false);
    }
  }, []);

  const fetchPrograms = useCallback(async () => {
    try {
      setProgramsLoading(true);
      const { data } = await bhcalc.get('/program');
      setPrograms(data);
    } catch (err) {
      console.error('Error fetching programs:', err);
    } finally {
      setProgramsLoading(false);
    }
  }, []);

  const fetchPartners = useCallback(async () => {
    try {
      setPartnersLoading(true);
      const { data } = await bhcalc.get('/partner');
      setPartners(data);
    } catch (err) {
      console.error('Error fetching partners:', err);
    } finally {
      setPartnersLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLocations();
    fetchPrograms();
    fetchProgramTypes();
    fetchPartners();
  }, [fetchLocations, fetchPrograms, fetchProgramTypes, fetchPartners]);

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setLocationFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setLogoFile(null);
      setLogoPreview(null);
    }
  };

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    setLocationError('');
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('name', locationFormData.name);
      formData.append('address', locationFormData.address);
      formData.append('zipcode', locationFormData.zipcode);
      if (locationFormData.logoUrl) {
        formData.append('logoUrl', locationFormData.logoUrl);
      }
      if (logoFile) {
        formData.append('logo', logoFile);
      }

      const { data } = await bhcalc.post('/location', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Location created:', data);

      // Close modal and reset form
      setShowLocationModal(false);
      setLocationFormData({ name: '', address: '', zipcode: '', logoUrl: '' });
      setLogoFile(null);
      setLogoPreview(null);

      // Refresh the locations table
      fetchLocations();
    } catch (err) {
      console.error('Error creating location:', err);
      if (err.response?.status === 400) {
        setLocationError('Invalid location data. Please check your inputs.');
      } else if (err.response?.status === 401) {
        setLocationError('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setLocationError('You do not have permission to create locations.');
      } else if (err.response?.status === 500) {
        setLocationError('Server error. Please try again later.');
      } else {
        setLocationError('Unable to create location. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationCancel = () => {
    setShowLocationModal(false);
    setLocationFormData({ name: '', address: '', zipcode: '', logoUrl: '' });
    setLogoFile(null);
    setLogoPreview(null);
    setLocationError('');
  };

  const handleProgramChange = (e) => {
    const { name, value } = e.target;
    setProgramFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProgramImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProgramImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProgramImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      setProgramImageFile(null);
      setProgramImagePreview(null);
    }
  };

  const handleProgramSubmit = async (e) => {
    e.preventDefault();
    setProgramError('');
    setIsProgramSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('programName', programFormData.programName);
      formData.append('programType', programFormData.programType);
      if (programImageFile) {
        formData.append('image', programImageFile);
      }

      const { data } = await bhcalc.post('/program', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Program created:', data);

      // Close modal and reset form
      setShowProgramModal(false);
      setProgramFormData({
        programName: '',
        programType: ''
      });
      setProgramImageFile(null);
      setProgramImagePreview(null);

      // Refresh the programs table and program types (in case a new type was created)
      fetchPrograms();
      fetchProgramTypes();
    } catch (err) {
      console.error('Error creating program:', err);
      if (err.response?.status === 400) {
        setProgramError('Invalid program data. Please check your inputs.');
      } else if (err.response?.status === 401) {
        setProgramError('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setProgramError('You do not have permission to create programs.');
      } else if (err.response?.status === 500) {
        setProgramError('Server error. Please try again later.');
      } else {
        setProgramError('Unable to create program. Please try again.');
      }
    } finally {
      setIsProgramSubmitting(false);
    }
  };

  const handleProgramCancel = () => {
    setShowProgramModal(false);
    setProgramFormData({
      programName: '',
      programType: ''
    });
    setProgramImageFile(null);
    setProgramImagePreview(null);
    setProgramError('');
  };

  const handlePartnerChange = (e) => {
    const { name, value } = e.target;
    setPartnerFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePartnerSubmit = async (e) => {
    e.preventDefault();
    setPartnerError('');
    setIsPartnerSubmitting(true);

    try {
      const { data } = await bhcalc.post('/partner', {
        email: partnerFormData.email,
        firstName: partnerFormData.firstName,
        lastName: partnerFormData.lastName,
        locationId: partnerFormData.locationId ? parseInt(partnerFormData.locationId) : undefined
      });
      console.log('Partner created:', data);

      // Close modal and reset form
      setShowPartnerModal(false);
      setPartnerFormData({
        email: '',
        firstName: '',
        lastName: '',
        locationId: ''
      });

      // Refresh the partners table
      fetchPartners();
    } catch (err) {
      console.error('Error creating partner:', err);
      if (err.response?.status === 400) {
        setPartnerError('Invalid partner data. Please check your inputs.');
      } else if (err.response?.status === 401) {
        setPartnerError('Session expired. Please log in again.');
      } else if (err.response?.status === 403) {
        setPartnerError('You do not have permission to create partners.');
      } else if (err.response?.status === 500) {
        setPartnerError('Server error. Please try again later.');
      } else {
        setPartnerError('Unable to create partner. Please try again.');
      }
    } finally {
      setIsPartnerSubmitting(false);
    }
  };

  const handlePartnerCancel = () => {
    setShowPartnerModal(false);
    setPartnerFormData({
      email: '',
      firstName: '',
      lastName: '',
      locationId: ''
    });
    setPartnerError('');
  };

  return (
    <div className="App min-h-screen">
      <div className="dashboard-wrapper flex flex-col min-h-screen">
        <div className="dashboard-header flex w-full max-h-24 h-14">
          <DashboardHeader />
        </div>
        <div className="w-full flex flex-wrap min-h-screen">
          <div className="dashboard-left-sidebar flex-none w-48 relative">
            <VerticalNavigation />
          </div>
          <div className="dashboard-right-main border-1 flex-auto p-6">
            <h2 className="text-2xl font-bold text-left mb-6">Administration</h2>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Locations</h3>
                <button
                  onClick={() => setShowLocationModal(true)}
                  className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                >
                  <span className="text-xl">+</span>
                  <span>Add Location</span>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Address</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Zipcode</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Logo</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-left">
                    {locationsLoading ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                          Loading locations...
                        </td>
                      </tr>
                    ) : locations.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                          No locations found. Add your first location above.
                        </td>
                      </tr>
                    ) : (
                      locations.map((location) => (
                        <tr key={location.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{location.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.address}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.zipcode}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{location.logoUrl || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => navigate(`/location/${location.id}`)}
                              className="text-blue-600 hover:text-blue-900 mr-3"
                            >
                              Manage
                            </button>
                            <button className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {locations.length > 0 && (
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-600">Showing {locations.length} location{locations.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Programs</h3>
                <button
                  onClick={() => setShowProgramModal(true)}
                  className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                >
                  <span className="text-xl">+</span>
                  <span>Add Program</span>
                </button>
              </div>

              {(() => {
                const today = new Date().toISOString().slice(0, 10);
                const isEffectivelyArchived = (p) => p.isArchived || (p.endDate && p.endDate < today);
                const activePrograms = programs.filter(p => !isEffectivelyArchived(p));
                const archivedPrograms = programs.filter(p => isEffectivelyArchived(p));
                const displayedPrograms = programTab === 'active' ? activePrograms : archivedPrograms;
                return (
                  <>
                    <div className="border-b border-gray-200 mb-4">
                      <nav className="-mb-px flex space-x-8">
                        <button
                          onClick={() => setProgramTab('active')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            programTab === 'active'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Active ({activePrograms.length})
                        </button>
                        <button
                          onClick={() => setProgramTab('archived')}
                          className={`py-2 px-1 border-b-2 font-medium text-sm ${
                            programTab === 'archived'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Archived ({archivedPrograms.length})
                        </button>
                      </nav>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Program Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Image</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 text-left">
                          {programsLoading ? (
                            <tr>
                              <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                                Loading programs...
                              </td>
                            </tr>
                          ) : displayedPrograms.length === 0 ? (
                            <tr>
                              <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500">
                                No {programTab} programs found.
                              </td>
                            </tr>
                          ) : (
                            displayedPrograms.map((program) => (
                              <tr key={program.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{program.programName}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{program.programType?.name || '-'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                  {program.imageUrl ? (
                                    <img src={program.imageUrl} alt={program.programName} className="h-8 w-8 object-cover rounded" />
                                  ) : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                                  <button className="text-red-600 hover:text-red-900">Delete</button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                    {displayedPrograms.length > 0 && (
                      <div className="flex justify-between items-center mt-4">
                        <span className="text-sm text-gray-600">Showing {displayedPrograms.length} {programTab} program{displayedPrograms.length !== 1 ? 's' : ''}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="mb-8">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Partners</h3>
                <button
                  onClick={() => setShowPartnerModal(true)}
                  className="px-3 py-1 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center space-x-2"
                >
                  <span className="text-xl">+</span>
                  <span>Add Partner</span>
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">First Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Last Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 text-left">
                    {partnersLoading ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                          Loading partners...
                        </td>
                      </tr>
                    ) : partners.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-sm text-gray-500">
                          No partners found. Add your first partner above.
                        </td>
                      </tr>
                    ) : (
                      partners.map((partner) => (
                        <tr key={partner.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{partner.firstName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{partner.lastName}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{partner.email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{partner.location?.name || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button className="text-blue-600 hover:text-blue-900 mr-3">Edit</button>
                            <button className="text-red-600 hover:text-red-900">Delete</button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {partners.length > 0 && (
                <div className="flex justify-between items-center mt-4">
                  <span className="text-sm text-gray-600">Showing {partners.length} partner{partners.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showLocationModal && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Location</h2>
                <button
                  onClick={handleLocationCancel}
                  disabled={isSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleLocationSubmit}>
                {locationError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {locationError}
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={locationFormData.name}
                      onChange={handleLocationChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter location name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address *
                    </label>
                    <input
                      type="text"
                      name="address"
                      value={locationFormData.address}
                      onChange={handleLocationChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter street address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Zipcode *
                    </label>
                    <input
                      type="text"
                      name="zipcode"
                      value={locationFormData.zipcode}
                      onChange={handleLocationChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter zipcode"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Logo URL
                    </label>
                    <input
                      type="text"
                      name="logoUrl"
                      value={locationFormData.logoUrl}
                      onChange={handleLocationChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter logo URL (optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload Logo Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {logoPreview && (
                      <div className="mt-2">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="max-h-32 rounded-md border border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleLocationCancel}
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Adding...' : 'Add Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showProgramModal && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Program</h2>
                <button
                  onClick={handleProgramCancel}
                  disabled={isProgramSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handleProgramSubmit}>
                {programError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {programError}
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Program Name *
                    </label>
                    <input
                      type="text"
                      name="programName"
                      value={programFormData.programName}
                      onChange={handleProgramChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter program name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Program Type *
                    </label>
                    <input
                      type="text"
                      name="programType"
                      value={programFormData.programType}
                      onChange={handleProgramChange}
                      required
                      list="programTypesList"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Select or enter a new program type"
                    />
                    <datalist id="programTypesList">
                      {programTypes.map((type) => (
                        <option key={type.id} value={type.name} />
                      ))}
                    </datalist>
                    <p className="mt-1 text-xs text-gray-500">
                      Select an existing type or enter a new one to create it
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Program Image
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProgramImageChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {programImagePreview && (
                      <div className="mt-2">
                        <img
                          src={programImagePreview}
                          alt="Program preview"
                          className="max-h-32 rounded-md border border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handleProgramCancel}
                    disabled={isProgramSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isProgramSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isProgramSubmitting ? 'Adding...' : 'Add Program'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showPartnerModal && (
        <div className="fixed text-left inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Add New Partner</h2>
                <button
                  onClick={handlePartnerCancel}
                  disabled={isPartnerSubmitting}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ×
                </button>
              </div>

              <form onSubmit={handlePartnerSubmit}>
                {partnerError && (
                  <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {partnerError}
                  </div>
                )}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={partnerFormData.firstName}
                      onChange={handlePartnerChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter first name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={partnerFormData.lastName}
                      onChange={handlePartnerChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter last name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={partnerFormData.email}
                      onChange={handlePartnerChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Location *
                    </label>
                    <select
                      name="locationId"
                      value={partnerFormData.locationId}
                      onChange={handlePartnerChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select a location</option>
                      {locations.map((location) => (
                        <option key={location.id} value={location.id}>
                          {location.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                  <button
                    type="button"
                    onClick={handlePartnerCancel}
                    disabled={isPartnerSubmitting}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isPartnerSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isPartnerSubmitting ? 'Adding...' : 'Add Partner'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Admin;
